import _ from 'lodash';
import Injector from 'vue-inject';
import Debug from '@/util/debug';
import moment from 'moment-timezone';
import addDays from '@/util/add-days';
import { getNextIterationStartDate } from '@/models/campaign';
import * as constants from '@/util/constants';

const debug = Debug('service:Statuses');

export default class StatusesService {
  constructor(adApiService, genericConfigService, statusFlowService) {
    this.adApiService = adApiService;
    this.genericConfigService = genericConfigService;
    this.statusFlowService = statusFlowService;
    this.statuses = null;
    this.error = null;
  }

  loadStatusesFromApi() {
    debug.info('loadStatusesFromApi');
    return this.adApiService.getStatuses()
      .then(statuses => this.statuses = statuses)
      .catch(err => {
        debug.error('error while loading from api: %o', err);
        this.error = err;
        throw err;
      });
  }

  getStatuses() {
    debug.info('getStatuses err: %o, statuses: %o', this.error, this.statuses);
    if (this.error !== null) {
      return new Promise((resolve, reject) => reject(this.error));
    } else if (this.statuses) {
      return new Promise(resolve => resolve(this.statuses));
    }

    return this.loadStatusesFromApi();
  }

  getStatusFlowLifecycle(flowTag) {
    return Promise.all([
      this.statusFlowService.isStatusFlowAvailable(flowTag),
      this.statusFlowService.getStatusFlows(),
      this.getStatuses(),
    ])
      .then(([available, flows, statuses]) => {
        if (!available) {
          return 0;
        }

        const flow = flows.find(i => i.tag === flowTag);
        if (flow && flow.overwritten_duration) {
          return flow.overwritten_duration;
        }

        return statuses[flowTag].reduce((days, s) => (days + s.life_stage), 0);
      });
  }

  getFirstAvailableStartDate(flowTag, newCreative = true, originalStartDate = null) {
    return Promise.all([
      this.getStatusFlowLifecycle(flowTag),
      this.genericConfigService.getConfig().then(c => c.use_working_days || false),
      this.genericConfigService.getConfig().then(c => c.holidays || []),
      this.genericConfigService.getConfig().then(c => _.get(c, 'campaign_subscription.period_length', constants.CAMPAIGN_SUBSCRIPTION.periodLength)),
      this.genericConfigService.getConfig().then(c => {
        const periodType = _.get(c, 'campaign_subscription.period_type', constants.CAMPAIGN_SUBSCRIPTION.periodType);
        if (!constants.CAMPAIGN_SUBSCRIPTION.periodTypes.includes(periodType)) {
          return null;
        }
        return periodType;
      }),
    ])
      .then(([days, onlyWorking, holidays, periodLength, periodType]) => {
        const now = moment();
        let minStart = null;
        if (originalStartDate !== null) {
          const campaignStartDate = moment(originalStartDate);
          let minCampaignStart = now.clone().add(constants.CAMPAIGN_SUBSCRIPTION.minChangePriceInterval, 'days');
          if (newCreative) {
            minCampaignStart = addDays(now, days, onlyWorking, holidays);
          }
          minStart = getNextIterationStartDate(campaignStartDate, periodType,
            periodLength, minCampaignStart);
        } else {
          minStart = addDays(now, days, onlyWorking, holidays)
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0);
        }

        debug.info('getFirstAvailableStartDate for %s = %s',
          flowTag,
          minStart.format('DD.MM.YYYY'),
        );

        return minStart;
      });
  }
}

Injector.service('statusesService', ['adApiService', 'genericConfigService', 'statusFlowService'], StatusesService);



// WEBPACK FOOTER //
// ./src/services/StatusesService.js